import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronDown, LogOut, Menu, RefreshCw, Settings } from 'lucide-react';
import { EditRequestDialog } from '@/components/review/EditRequestDialog';
import { NoticeBell } from '@/components/NoticeBell';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/context/PermissionsContext';
import { getTimezone, nowDate, onClockChange } from '@/lib/clock';
import { avatarColor, initials } from '@/components/tasks/taskShared';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

// Reloj vivo en la zona horaria del workspace (ajustado al reloj del servidor)
const Clock = () => {
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick((n) => n + 1), 1000); const off = onClockChange(() => tick((n) => n + 1)); return () => { clearInterval(id); off(); }; }, []);
  const tz = getTimezone(); const d = nowDate();
  const raw = d.toLocaleDateString('es-MX', { timeZone: tz, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const date = raw.charAt(0).toUpperCase() + raw.slice(1);
  const time = d.toLocaleTimeString('es-MX', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const gmt = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(d).find((x) => x.type === 'timeZoneName')?.value || '';
  return (
    <div className="min-w-0 leading-tight" data-testid="topbar-clock">
      <p className="truncate text-sm font-bold" data-testid="topbar-date">{date}</p>
      <p className="font-mono text-[11px] text-white/70" data-testid="topbar-time">{time}{gmt ? ` · ${gmt}` : ''}</p>
    </div>
  );
};

const Avatar = ({ name, url, className }) => url
  ? <img src={url} alt={name} className={`${className} rounded-full object-cover ring-2 ring-white/20`} />
  : <span className={`${className} inline-flex items-center justify-center rounded-full font-bold text-white ring-2 ring-white/20 ${avatarColor(name)}`}>{initials(name)}</span>;

// Barra superior: sección actual, campana de pendientes y menú de perfil (Cerrar sesión)
export const TopBar = ({ onMenu }) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { me } = usePermissions();
  const name = me?.full_name?.trim() || me?.email || 'Usuario';
  const logout = async () => { await signOut(); navigate('/login'); };
  const { pathname } = useLocation();
  const [report, setReport] = useState(null);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 bg-inmoblum-blue px-4 text-white shadow-md lg:px-6" data-testid="topbar">
      <button type="button" onClick={onMenu} className="rounded-lg p-1.5 hover:bg-white/10 lg:hidden" aria-label="Abrir menú" data-testid="topbar-menu-btn"><Menu className="h-5 w-5" /></button>
      <Clock />
      <div className="ml-auto flex items-center gap-3">
        <button type="button" onClick={() => window.location.reload()} className="group rounded-full p-2 transition hover:bg-white/10" title="Recargar" aria-label="Recargar" data-testid="topbar-refresh"><RefreshCw className="h-5 w-5 transition-transform duration-500 group-hover:rotate-180" /></button>
        <NoticeBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="flex items-center gap-1 rounded-full transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40" data-testid="topbar-profile-btn">
              <Avatar name={name} url={me?.avatar_url} className="h-9 w-9 text-xs" />
              <span className="hidden max-w-[180px] truncate pl-1 text-sm font-semibold md:block" data-testid="topbar-user-name">{name}</span>
              <ChevronDown className="mr-1 hidden h-4 w-4 text-white/70 md:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 p-2" data-testid="topbar-profile-menu">
            <div className="flex items-center gap-3 px-2 py-2">
              <Avatar name={name} url={me?.avatar_url} className="h-10 w-10 text-sm" />
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-900" data-testid="topbar-profile-name">{name}</p>
                <p className="truncate text-xs text-slate-500" data-testid="topbar-profile-email">{me?.email}</p>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/app/settings')} className="gap-2 text-slate-700" data-testid="topbar-settings-item"><Settings className="h-4 w-4" />Configuración</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setReport({ kind: 'system', type: 'error', summary: `Pantalla: ${pathname}` })} className="gap-2 text-amber-700 focus:text-amber-800" data-testid="topbar-report-error-item"><AlertCircle className="h-4 w-4" />Reportar error del sistema</DropdownMenuItem>
            <DropdownMenuItem onClick={logout} className="mt-1 justify-center gap-2 rounded-lg bg-rose-50 font-semibold text-rose-700 focus:bg-rose-100 focus:text-rose-800" data-testid="topbar-logout-btn"><LogOut className="h-4 w-4" />Cerrar sesión</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <EditRequestDialog target={report} onClose={() => setReport(null)} />
    </header>
  );
};

export default TopBar;
